if (!customElements.get('theme-purchase-offers')) {
  customElements.define('theme-purchase-offers', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.section = this.closest('product-info') || this.closest('.shopify-section');
      this.form = this.section?.querySelector('form[data-type="add-to-cart-form"]');
      this.product = JSON.parse(this.querySelector('[data-offer-product]').textContent);
      this.offers = [...this.querySelectorAll('[data-offer]')];
      for (const offer of this.offers) {
        for (const element of offer.querySelectorAll('[data-offer-unit-index]')) {
          const active = Number(element.dataset.offerUnitIndex) <= Number(offer.dataset.quantity);
          element.hidden = !active;
          if (element.matches('select')) element.disabled = !active;
        }
      }
      this.money = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-offer-select]');
        const card = event.target.closest('[data-offer]');
        if (button && !button.disabled) this.select(button.closest('[data-offer]'));
        else if (card && card.dataset.available !== 'false' && !event.target.closest('a, button, input, select, textarea, summary')) this.select(card);
      }, options);
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-offer-variant]')) event.target.dataset.touched = 'true';
        this.refresh();
      }, options);
      this.section?.addEventListener('change', (event) => {
        if (event.target.name === 'quantity' && !event.detail?.purchaseOffer) {
          const match = this.offers.find((offer) => Number(offer.dataset.quantity) === Number(event.target.value));
          if (match) this.select(match);
          else { this.selected = null; this.refresh(); }
        }
        if (['id', 'selling_plan'].includes(event.target.name)) this.refresh();
      }, options);
      this.section?.addEventListener('click', (event) => {
        if (event.target.closest('theme-subscription')) queueMicrotask(() => this.refresh());
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const offer = event.target.closest('[data-offer]');
        if (offer && this.contains(offer)) this.select(offer);
      }, options);
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const originalSection = this.section?.dataset.originalSection;
          const sectionId = originalSection || this.section?.dataset.section || this.section?.id.replace('shopify-section-', '');
          if (data.sectionId !== sectionId) return;
          const prefix = `PurchaseOffers-${this.section?.dataset.section}-`;
          const sourceId = originalSection && this.id.startsWith(prefix) ? `PurchaseOffers-${originalSection}-${this.id.slice(prefix.length)}` : this.id;
          const nextOffer = data.html.getElementById(sourceId);
          const productId = this.section?.dataset.productId;
          if (productId && nextOffer?.closest('product-info')?.dataset.productId !== productId) return;
          const next = nextOffer?.querySelector('[data-offer-product]');
          if (next) this.product = JSON.parse(next.textContent);
          if (data.variant) this.syncPickers(String(data.variant.id));
          this.refresh();
        });
      }
      this.refresh();
      const initial = this.offers[Number(this.dataset.default || 1) - 1];
      if (![...(this.section?.querySelectorAll('theme-purchase-offers') || [])].some((offer) => offer !== this && offer.isActive?.())) {
        this.select(initial && !initial.querySelector('button').disabled ? initial : this.offers.find((offer) => !offer.querySelector('button').disabled));
      }
      this.form?.addEventListener('submit', (event) => {
        if (this.isActive() && !this.validateSelection()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      this.restoreCheckout();
      this.hiddenQuantity?.remove();
    }

    syncPickers(variantId) {
      for (const picker of this.querySelectorAll('[data-offer-variant]:not([data-touched])')) {
        const option = [...picker.options].find((entry) => entry.value === variantId && !entry.disabled);
        if (option) picker.value = variantId;
      }
    }

    isActive() {
      return Boolean(this.selected && this.getClientRects().length);
    }

    getPlan() {
      const input = [...(this.form?.elements || [])].find((element) => element.name === 'selling_plan' && !element.disabled && (!['radio', 'checkbox'].includes(element.type) || element.checked));
      return input?.value || '';
    }

    getItems(offer) {
      if (!offer) return [];
      const plan = this.getPlan();
      const variantId = this.form?.querySelector('[name=id]')?.value || this.product.selected;
      const pickers = [...offer.querySelectorAll('[data-offer-variant]:not(:disabled)')];
      const items = this.dataset.variants === 'true' && offer.querySelector('[data-offer-variants-block]')?.dataset.enabled !== 'false' && !plan && pickers.length
        ? pickers.map((picker) => ({ id: picker.value, quantity: 1 }))
        : [{ id: variantId, quantity: Number(offer.dataset.quantity), ...(plan ? { selling_plan: plan } : {}) }];
      return window.ThemeOfferPricing.groupItems(items);
    }

    getTotals(offer) {
      const result = window.ThemeOfferPricing.calculate({
        items: this.getItems(offer), variants: this.product.variants,
        discountType: offer.dataset.discountType, discount: offer.dataset.discount,
        confirmed: offer.dataset.confirmed === 'true' && (offer.dataset.discountType !== 'fixed' || this.dataset.currency === this.dataset.storeCurrency),
      });
      if (this.product.requiresPlan && !this.getPlan()) result.available = false;
      for (const extra of offer.querySelectorAll('[data-offer-extra]:checked:not(:disabled)')) {
        result.price += Number(extra.dataset.price);
        result.compare += Math.max(Number(extra.dataset.compare), Number(extra.dataset.price));
      }
      return result;
    }

    select(offer) {
      if (!offer) return;
      this.selected = offer;
      for (const sibling of this.section?.querySelectorAll('theme-purchase-offers') || []) {
        if (sibling !== this && sibling.selected) { sibling.selected = null; sibling.refresh(); }
      }
      for (const other of this.offers) {
        if (other !== offer) other.querySelectorAll('[data-offer-extra]').forEach((input) => { input.checked = false; });
      }
      if (this.form && this.getClientRects().length) {
        let quantities = [...this.form.elements].filter((element) => element.name === 'quantity');
        if (!quantities.length) {
          this.hiddenQuantity = Object.assign(document.createElement('input'), { type: 'hidden', name: 'quantity' });
          this.form.append(this.hiddenQuantity);
          quantities = [this.hiddenQuantity];
        }
        for (const input of quantities) {
          input.value = offer.dataset.quantity;
          input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { purchaseOffer: true } }));
        }
      }
      this.refresh();
    }

    refresh() {
      for (const offer of this.offers) {
        const totals = this.getTotals(offer);
        const selected = this.selected === offer;
        const button = offer.querySelector('[data-offer-select]');
        button.disabled = !totals.available;
        button.setAttribute('aria-pressed', String(selected));
        offer.dataset.selected = String(selected);
        offer.dataset.available = String(totals.available);
        for (const element of offer.querySelectorAll('[data-offer-value]')) {
          const type = element.dataset.offerValue;
          const amounts = { price: totals.price, compare: totals.compare, savings: totals.compare - totals.price, unit: totals.price / Number(offer.dataset.quantity) };
          element.hidden = ['compare', 'savings'].includes(type) && totals.compare <= totals.price;
          const amount = element.querySelector('[data-offer-amount]');
          if (amount) amount.textContent = this.money.format((amounts[type] || 0) / 100);
        }
        const availability = offer.querySelector('[data-offer-availability]');
        if (availability) availability.hidden = totals.available;
        const variants = offer.querySelector('[data-offer-variants]');
        const variantsBlock = offer.querySelector('[data-offer-variants-block]');
        const hasVariants = variants && this.dataset.variants === 'true' && !this.getPlan() && variantsBlock?.dataset.enabled !== 'false';
        if (variants) variants.hidden = !hasVariants;
        if (variantsBlock) variantsBlock.hidden = !selected || !hasVariants;
        const extras = offer.querySelector('[data-offer-extras-block]');
        if (extras) extras.hidden = !selected || !extras.querySelector('[data-offer-extra]');
        button.setAttribute('aria-label', `${offer.querySelector('.theme-text-block .theme-text__content')?.textContent.trim() || offer.dataset.quantity} — ${this.money.format(totals.price / 100)}`);
      }
      this.updateCheckout();
      const error = this.querySelector('[data-offer-error]');
      if (!this.selected || this.getTotals(this.selected).available) error.hidden = true;
      this.dispatchEvent(new CustomEvent('theme:offer-change', { bubbles: true }));
    }

    validateSelection() {
      this.refresh();
      if (this.getTotals(this.selected).available) return true;
      const error = this.querySelector('[data-offer-error]');
      error.textContent = this.dataset.error;
      error.hidden = false;
      this.form?.closest('product-form')?.handleErrorMessage(this.dataset.error);
      return false;
    }

    prepareCartItems(formData) {
      const items = this.getItems(this.selected);
      for (const extra of this.selected.querySelectorAll('[data-offer-extra]:checked:not(:disabled)')) {
        items.push({ id: extra.value, quantity: 1, extra: true });
      }
      window.ThemeOfferPricing.writeItems(formData, items);
      return items;
    }

    updateCheckout() {
      const checkout = this.form?.querySelector('.shopify-payment-button');
      const hasBundle = this.isActive() && (this.getItems(this.selected).length > 1 || this.selected.querySelector('[data-offer-extra]:checked'));
      if (checkout && hasBundle) {
        if (!this.checkout) { this.checkout = checkout; this.checkoutWasHidden = checkout.hidden; }
        checkout.hidden = true;
      } else this.restoreCheckout();
    }

    restoreCheckout() {
      if (this.checkout) { this.checkout.hidden = this.checkoutWasHidden; this.checkout = null; }
    }
  });
}
